import { AGG_LABELS } from './describe'
import type { Bucket, CatalogSet, Definition, Display, Filter, ResultColumn, RunResult, Words } from './types'

/**
 * DOUA LUCRURI CARE TIN DE FORMA REZULTATULUI, SCRISE O DATA:
 *
 *  - coloanele pe care le va intoarce motorul (`g0`, `m0`, `c0`...), cu
 *    eticheta lor, inainte de rulare - pentru numele date de om si pentru
 *    regulile de culoare din constructor, si ca sa nu plece la server un nume
 *    sau o regula pe o coloana care nu mai exista (`Compiler::display` le-ar
 *    refuza);
 *  - "arata-mi randurile" dintr-o bara sau un rand grupat: aceeasi definitie,
 *    fara grupare, cu valorile grupului puse ca filtre.
 */

export interface PlannedColumn {
  key: string
  role: 'group' | 'measure' | 'column'
  label: Words
  numeric: boolean
}

const NUMERIC = ['number', 'duration', 'bytes', 'percent']

/** Coloanele rezultatului, in ordinea motorului (`Compiler::compile`). */
export function plannedColumns(def: Definition, set: CatalogSet): PlannedColumn[] {
  const field = (key: string | undefined) => set.fields.find((f) => f.key === key)
  const groups = def.group_by ?? []
  const measures = def.measures && def.measures.length > 0 ? def.measures : groups.length > 0 ? [{ agg: 'count' as const }] : []
  if (groups.length === 0 && measures.length === 0) {
    return (def.columns ?? set.columns).map((key, i) => {
      const f = field(key)
      return { key: `c${i}`, role: 'column', label: f?.label ?? { ro: key, en: key }, numeric: NUMERIC.includes(f?.type ?? '') }
    })
  }
  const out: PlannedColumn[] = groups.map((g, i) => ({ key: `g${i}`, role: 'group', label: field(g.field)?.label ?? { ro: g.field, en: g.field }, numeric: false }))
  measures.forEach((m, i) => {
    const f = field(m.field)
    const label: Words = f ? { ro: `${AGG_LABELS[m.agg].ro} ${f.label.ro.toLowerCase()}`, en: `${AGG_LABELS[m.agg].en} ${f.label.en.toLowerCase()}` } : { ro: 'Numar', en: 'Count' }
    out.push({ key: `m${i}`, role: 'measure', label, numeric: true })
  })
  return out
}

/** Numele si regulile ramase pe coloane care nu mai exista ies din definitie. */
export function pruneDisplay(def: Definition, set: CatalogSet): Definition {
  if (!def.display) return def
  const keys = plannedColumns(def, set)
  const exists = (k: string) => keys.some((c) => c.key === k)
  const numeric = (k: string) => keys.some((c) => c.key === k && c.numeric && c.role !== 'group')
  const display: Display = { ...def.display }
  if (display.labels) {
    const labels = Object.fromEntries(Object.entries(display.labels).filter(([k, v]) => exists(k) && v.trim() !== ''))
    if (Object.keys(labels).length > 0) display.labels = labels
    else delete display.labels
  }
  if (display.rules) {
    const rules = display.rules.filter((r) => numeric(r.col) && Number.isFinite(r.value))
    if (rules.length > 0) display.rules = rules
    else delete display.rules
  }
  for (const flag of ['totals', 'others', 'share', 'compare'] as const) if (display[flag] === false) delete display[flag]
  const next: Definition = { ...def }
  if (Object.keys(display).length > 0) next.display = display
  else delete next.display
  return next
}

/** Coloanele `c*` mutate sau scoase: numele si regulile se muta cu ele. */
export function remapColumns(def: Definition, before: string[], after: string[]): Display | undefined {
  if (!def.display) return undefined
  const move = (alias: string): string | null => {
    const m = /^c(\d+)$/.exec(alias)
    if (!m) return alias
    const field = before[Number(m[1])]
    const at = field === undefined ? -1 : after.indexOf(field)
    return at < 0 ? null : `c${at}`
  }
  const display: Display = { ...def.display }
  if (display.labels) {
    const labels: Record<string, string> = {}
    for (const [k, v] of Object.entries(display.labels)) {
      const to = move(k)
      if (to) labels[to] = v
    }
    display.labels = labels
  }
  if (display.rules) display.rules = display.rules.flatMap((r) => (move(r.col) ? [{ ...r, col: move(r.col)! }] : []))
  return display
}

function pad(n: number): string {
  return String(n).padStart(2, '0')
}

function iso(d: Date): string {
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`
}

/**
 * Felia de timp scrisa de motor (`2026-09-14`, `2026-W38`, `2026-09`,
 * `2026-Q3`, `2026`) ca zile, `to` inclusiv - forma filtrului `between`.
 */
export function bucketRange(label: string, bucket: Bucket): [string, string] | null {
  const utc = (y: number, m: number, d: number) => new Date(Date.UTC(y, m, d))
  let m: RegExpExecArray | null
  switch (bucket) {
    case 'day':
      return /^\d{4}-\d{2}-\d{2}$/.test(label) ? [label, label] : null
    case 'week': {
      m = /^(\d{4})-W(\d{1,2})$/.exec(label)
      if (!m) return null
      /* Saptamana ISO: cea care tine 4 ianuarie e prima. */
      const jan4 = utc(Number(m[1]), 0, 4)
      const monday = utc(Number(m[1]), 0, 4 - ((jan4.getUTCDay() + 6) % 7) + (Number(m[2]) - 1) * 7)
      return [iso(monday), iso(utc(monday.getUTCFullYear(), monday.getUTCMonth(), monday.getUTCDate() + 6))]
    }
    case 'month':
      m = /^(\d{4})-(\d{2})$/.exec(label)
      return m ? [iso(utc(Number(m[1]), Number(m[2]) - 1, 1)), iso(utc(Number(m[1]), Number(m[2]), 0))] : null
    case 'quarter':
      m = /^(\d{4})-Q([1-4])$/.exec(label)
      return m ? [iso(utc(Number(m[1]), (Number(m[2]) - 1) * 3, 1)), iso(utc(Number(m[1]), Number(m[2]) * 3, 0))] : null
    case 'year':
      return /^\d{4}$/.test(label) ? [`${label}-01-01`, `${label}-12-31`] : null
  }
  return null
}

/**
 * Definitia randurilor din spatele unui rand grupat (o bara, o felie, o
 * celula): aceeasi perioada, aceleasi restrangeri si filtre, plus cate un
 * filtru pe fiecare grup. Null cand nu se poate spune exact (un grup mascat,
 * o felie de timp necunoscuta, filtre "oricare" pe care un filtru in plus le-ar
 * largi in loc sa le ingusteze).
 */
export function drillDefinition(def: Definition, set: CatalogSet, result: RunResult, rowIndex: number): Definition | null {
  const row = result.rows[rowIndex]
  if (!row) return null
  const groups = result.columns.map((c, i) => ({ c, i })).filter((x) => x.c.role === 'group')
  if (groups.length === 0) return null
  if (def.match === 'any' && (def.filters?.length ?? 0) > 1) return null
  const added: Filter[] = []
  for (const { c, i } of groups) {
    const f = drillFilter(c, set, row.values[i] ?? null, row.ids[c.key])
    if (!f) return null
    added.push(f)
  }
  const filters = [...(def.filters ?? []), ...added]
  if (filters.length > 20) return null
  const next: Definition = { dataset: def.dataset, view: 'table', filters }
  if (def.restrict) next.restrict = def.restrict
  if (def.period) next.period = def.period
  if (def.time_field) next.time_field = def.time_field
  return next
}

function drillFilter(column: ResultColumn, set: CatalogSet, value: string | number | null, id: number | null | undefined): Filter | null {
  const field = set.fields.find((f) => f.key === column.field)
  if (!field || column.masked || value === '***' || !field.filter) return null
  if (id !== undefined) return id === null ? { field: field.key, op: 'empty' } : { field: field.key, op: 'in', value: [id] }
  if (value === null || value === '') return { field: field.key, op: 'empty' }
  if (field.type === 'date') {
    const range = bucketRange(String(value), column.bucket ?? 'day')
    return range ? { field: field.key, op: 'between', value: range } : null
  }
  if (field.type === 'bool') return { field: field.key, op: 'eq', value: Number(value) }
  if (['number', 'duration', 'bytes'].includes(field.type)) return typeof value === 'number' ? { field: field.key, op: 'eq', value } : null
  return { field: field.key, op: 'eq', value: String(value) }
}
